import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionableNotification, Checkbox, Modal, Tag } from '@carbon/react';
import { definePage, defineResource, TenantParams } from '@restavo/crud';
import EmptyState from 'src/components/Base/EmptyState';
import EmployeeForm from 'src/components/Employee/EmployeeForm';
import EmployeeInviteModal from 'src/components/Employee/EmployeeInviteModal';
import { useTenantContext } from 'src/context/useTenantContext';
import { useAuthSession } from 'src/hooks/useAuthSession';
import { useRemoveMember } from 'src/hooks/useOrgMembers';
import { useMemberships } from 'src/hooks/useOrganizations';
import { useCurrentRestaurant } from 'src/hooks/useRestaurants';
import {
  orgRestaurantBranches,
  tenantEmployeeCreate,
  tenantEmployeeDelete,
  tenantEmployeeUpdate,
  tenantEmployees,
} from 'src/services/apis/tenantRequests';
import { RestaurantScope } from 'src/services/apis/tenantRequests';
import { EmployeeCreateDto, EmployeeEntity, EmployeeUpdateDto } from 'src/services/apis/types';
import { accountStatusOf, AccountStatus } from 'src/utils/accountStatus';
import { EmployeeRoles } from 'src/utils/employee';
import { QUERIES } from 'src/utils/react-query';

/**
 * Module-scope so the branch filter chip and the branch column share one options fetch
 * (spec §7.5). The generated branches wrapper takes no query params and needs none: the
 * server returns every branch, which is the scale the reference cache expects.
 */
const branchReference = (params: TenantParams) => orgRestaurantBranches(params);

/**
 * The name/phone/email cells (and the in-memory search) read the user identity the server
 * eagerly joins onto every employee row — the hand-written page spread those fields onto
 * each row before rendering, so the list wrapper keeps doing exactly that. An employee hired
 * without a portal account has no `user` relation at all (`userId` is null), so every read
 * below falls back to the employment record's own copy of the field; the linked auth-backed
 * user wins when present because auth is authoritative for identity (spec decision 6).
 */
export type EmployeeRow = EmployeeEntity & {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
};

/**
 * Exported (not inlined into `api.list`) so the account-less-row read rule is unit-testable
 * without standing up the whole CRUD page: an employee hired without a portal account has no
 * `user` relation, and this is the one place that reconciles the joined-user fields against
 * the employment record's own copies.
 */
export function toEmployeeRow(item: EmployeeEntity): EmployeeRow {
  return {
    ...item,
    firstName: item.user?.firstName ?? item.firstName ?? '',
    lastName: item.user?.lastName ?? item.lastName ?? '',
    email: item.user?.email ?? item.email ?? '',
    phoneNumber: item.user?.phoneNumber ?? item.phoneNumber ?? '',
  };
}

/**
 * Shapes EmployeeForm's raw values into a request body. `mode`/`authUserId` are form-only
 * concepts EmployeeCreateDto doesn't carry as siblings of the contact fields: 'existing'
 * sends ONLY `authUserId` (the server resolves it against Users.authUserId and nulls any
 * contact columns on the linked row — Task 12a); 'new' (and every edit, where `mode` is
 * never set to 'existing' because the switch is hidden) keeps the contact fields as-is —
 * except an edit of a LINKED employee, whose contact details belong to their account: the
 * server never writes the account from an employee edit and strips all four from the
 * employment row, so sending them would only imply they save (EmployeeForm shows them
 * read-only). Its `userId` is still echoed; the server accepts the loaded value back.
 * Exported so every branch is unit-testable without mounting the form.
 */
export function shapeEmployeePayload(values: Record<string, unknown>): Record<string, unknown> {
  const { mode, authUserId, ...rest } = values;
  if (mode !== 'existing') {
    if (accountStatusOf({ userId: rest.userId as string | null }) !== 'active') {
      return rest;
    }
    const linkedEdit: Record<string, unknown> = { ...rest };
    delete linkedEdit.firstName;
    delete linkedEdit.lastName;
    delete linkedEdit.email;
    delete linkedEdit.phoneNumber;
    return linkedEdit;
  }
  const employment: Record<string, unknown> = { ...rest };
  delete employment.firstName;
  delete employment.lastName;
  delete employment.email;
  delete employment.phoneNumber;
  delete employment.userId;
  return { ...employment, authUserId };
}

/**
 * The trigger for the deactivate-with-revoke prompt (spec risk 4): an edit that turns OFF
 * `activated` on a row that currently has live portal access. Requires an actual true->false
 * transition — re-saving an already-inactive row must not re-prompt — and reads
 * accountStatusOf(row) rather than `row.userId` directly so the rule stays in one place.
 */
export function shouldConfirmDeactivate(row: EmployeeRow, values: { activated: boolean }): boolean {
  return row.activated !== false && values.activated === false && accountStatusOf(row) === 'active';
}

/**
 * Keyed by employee id, NOT a single slot: two different rows can be mid-revoke at once (an
 * owner working down a list of deactivations), and a single shared slot let one row's success
 * erase a completely different row's still-unresolved failure (spec risk 4, arriving through a
 * different door than the one the confirm dialog guards). Chosen over showing only the most
 * recent failure — every failed revoke gets its own notice, stacked, and its own retry.
 */
export type RevokeFailures = Record<string, { row: EmployeeRow; error: string }>;

/**
 * The revoke orchestration, pulled out of the hook so the "one row's success can't touch
 * another row's failure" invariant is unit-testable without mounting EmployeeForm or mocking
 * better-auth. `updateFailures` is Formik/React's own setState updater-function shape, so this
 * plugs into `setRevokeFailures` directly.
 */
export async function revokeEmployeeAccess(
  row: EmployeeRow,
  removeMember: (email: string) => Promise<void>,
  updateFailures: (updater: (prev: RevokeFailures) => RevokeFailures) => void
): Promise<void> {
  const email = row.user?.email ?? row.email;
  try {
    await removeMember(email);
    updateFailures((prev) => {
      if (!(row.id in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[row.id];
      return next;
    });
  } catch (error) {
    const message = (error as Error).message;
    updateFailures((prev) => ({ ...prev, [row.id]: { row, error: message } }));
  }
}

// Exported so the userId filter entry (below) is unit-testable without rendering the page.
export const employees = defineResource<EmployeeRow, EmployeeCreateDto, EmployeeUpdateDto, RestaurantScope>({
  key: QUERIES.EMPLOYEES,
  // The hand-written hook sent no limit, so the server (alwaysPaginate, no configured
  // take) returned every row in one shot; 1000 is the practical match — the same reach
  // the pilot used for an effectively-unbounded list.
  pageSize: 1000,
  api: {
    list: (scope: RestaurantScope, query) =>
      tenantEmployees(scope, query).then((result) => ({
        ...result,
        data: result.data.map(toEmployeeRow),
      })),
    create: tenantEmployeeCreate,
    update: tenantEmployeeUpdate,
    remove: tenantEmployeeDelete,
  },
  // The hand-written page filtered first/last name, email and phone in memory over the
  // fetched set — all four now live on the mapped row.
  search: 'client',
  searchFields: ['firstName', 'lastName', 'email', 'phoneNumber'],
  filterable: [
    {
      field: 'restaurantBranchId',
      labelKey: 'common.branch',
      type: 'reference',
      load: branchReference,
      optionLabel: (branch: any) => branch.name,
    },
    // `userId` is a plain column on Employees (no join). The generic text type's `isEmpty`
    // operator compiles to `userId||$isnull`, which is exactly "no linked account" — the
    // state accountStatusOf reports as 'none'/'invited'/'expired' (all three have no
    // userId). The inverse ("has an account") has no dedicated operator in this package's
    // filter vocabulary yet (no `isNotEmpty`/`$notnull`), and the invited/expired split
    // needs date arithmetic against "now" that no filter type here expresses — both stay
    // out of scope for this entry; only the isEmpty operator is a meaningful choice here.
    {
      field: 'userId',
      labelKey: 'common.account',
      type: 'text',
    },
  ],
});

/**
 * The AllEmployees page config, as a hook to match the pilot's shape (page-specific
 * runtime data derived from tenant context).
 *
 * The change-password row action/modal that used to live here is gone: the server-side
 * endpoint it called (`POST /employees/change-password`) was removed as part of the
 * employment-lifecycle work (it wrote `Users.password`, which nothing live reads anymore).
 * Its replacement is the single "Invite to portal" row action wired below: it opens
 * `EmployeeInviteModal`, which reads `accountStatusOf(row)` itself to decide whether it is
 * inviting, resending or (for an already-active account) just showing status.
 */
export const useEmployeesPage = () => {
  const currentRestaurant = useCurrentRestaurant();
  const { t } = useTranslation();
  const { orgSlug, restaurantId } = useTenantContext();
  const [invitingRow, setInvitingRow] = useState<EmployeeRow | null>(null);

  // Deactivate-with-revoke (spec risk 4): organizationId resolved the way
  // OrgSettings/Invitations.tsx and useEmployeeInvitations.ts do — match the active
  // session's organization against the membership list, falling back to the first one.
  const { activeOrganizationId } = useAuthSession();
  const { data: memberships } = useMemberships();
  const activeOrg = useMemo(
    () => memberships?.find((member) => member.id === activeOrganizationId) ?? memberships?.[0],
    [memberships, activeOrganizationId]
  );
  const organizationId = activeOrg?.id ?? null;
  const removeMember = useRemoveMember(organizationId);

  const [pendingDeactivate, setPendingDeactivate] = useState<{
    row: EmployeeRow;
    values: any;
    submit: (values: any) => Promise<void>;
  } | null>(null);
  const [revokeAccess, setRevokeAccess] = useState(true);
  // Keyed by employee id — see RevokeFailures — so a second row's revoke can't erase the
  // first row's still-unresolved failure notice.
  const [revokeFailures, setRevokeFailures] = useState<RevokeFailures>({});

  /**
   * CrudPage renders `presentation.form` as `<FormComponent ref={formRef} .../>` and gives
   * it no channel back except the `onSubmit` it passes in — no hook for "confirm before
   * this submission proceeds". So the guard has to live in the form slot itself, wrapping
   * EmployeeForm and intercepting its onSubmit before forwarding to CrudPage's own (which
   * runs the mutation and closes the modal).
   *
   * Built once via useState's lazy initializer (React's documented pattern for expensive
   * one-time construction — see "avoiding recreating the ref contents"), never rebuilt on a
   * later render: `setPendingDeactivate` is a stable setState identity, so the closure below
   * never goes stale. A component recreated every render here would change
   * `presentation.form`'s identity every render, which remounts EmployeeForm — and its live
   * Formik state — under CrudPage.
   */
  const [GuardedEmployeeForm] = useState(() => {
    const Guarded = React.forwardRef<
      any,
      { initialValues: EmployeeRow | null; onSubmit: (values: any) => Promise<void> }
    >((formProps, ref) => {
      const guardedSubmit = async (values: any) => {
        const row = formProps.initialValues;
        if (row && shouldConfirmDeactivate(row, values)) {
          // Reset to checked every time the dialog opens for a (possibly different) row —
          // "checked by default" per employee, not a leftover from whatever the last
          // deactivation was left at.
          setRevokeAccess(true);
          setPendingDeactivate({ row, values, submit: formProps.onSubmit });
          return;
        }
        await formProps.onSubmit(values);
      };
      return (
        <EmployeeForm ref={ref} initialValues={formProps.initialValues} onSubmit={guardedSubmit} errorMessage={null} />
      );
    });
    Guarded.displayName = 'EmployeeFormWithDeactivateGuard';
    return Guarded;
  });

  async function revoke(row: EmployeeRow) {
    await revokeEmployeeAccess(row, (email) => removeMember.mutateAsync(email), setRevokeFailures);
  }

  async function confirmDeactivate() {
    if (!pendingDeactivate) return;
    const { row, values, submit } = pendingDeactivate;
    setPendingDeactivate(null);
    // The HR update commits first — closing the edit modal on success — before the org
    // removal even starts, so a revoke failure below can never mask a failed deactivation.
    await submit(values);
    if (revokeAccess) {
      await revoke(row);
    }
  }

  const page = {
    ...definePage(employees, {
      titleKey: 'titles.employeeSettingsPage',
      descriptionKey: 'common.employees',
      createLabelKey: 'actions.createNewEmployee',
      updateLabelKey: 'actions.updateEmployee',
      deleteHeadingKey: 'messages.areYouSureToDeleteThisEmployee',
      columns: [
        // The column a user identifies an employee by must survive every prefs
        // reconciliation. Nothing here is sortable: firstName/lastName live on the joined
        // user, not on the employee table, so a sort could only 400 — and the hand-written
        // page had no sortable headers either.
        { key: 'firstName', headerKey: 'common.firstName', alwaysVisible: true },
        { key: 'lastName', headerKey: 'common.lastName' },
        { key: 'email', headerKey: 'common.email' },
        { key: 'phoneNumber', headerKey: 'common.phoneNumber' },
        {
          key: 'role',
          headerKey: 'common.role',
          // Same Tag, same EmployeeRoles lookup, same green-when-E coloring the
          // hand-written cell used — the labels are hardcoded English there, preserved.
          render: (row: EmployeeRow) => (
            <Tag type={row.role === 'E' ? 'green' : 'red'} size="sm">
              {EmployeeRoles.find((item) => item.value === row.role)?.label}
            </Tag>
          ),
        },
        {
          key: 'accountStatus',
          headerKey: 'common.account',
          // Derived, not stored — accountStatusOf reads `userId`/`invitedAt` straight off
          // the row; no separate server-side status field to fall out of sync with it.
          render: (row: EmployeeRow) => {
            const status: AccountStatus = accountStatusOf(row);
            const type = { active: 'green', invited: 'blue', expired: 'red', none: 'gray' }[status];
            return (
              <Tag type={type as 'green'} size="sm">
                {t(`employee.account.${status}`)}
              </Tag>
            );
          },
        },
        {
          key: 'restaurantBranchId',
          headerKey: 'common.branch',
          display: {
            kind: 'reference',
            load: branchReference,
            optionLabel: (branch: any) => branch.name,
          },
        },
        // The hand-written cell was a dead per-row Toggle (no onToggle handler, so it never
        // changed anything) — the same yes/no copy as static text, like the pilot's menus page.
        { key: 'activated', headerKey: 'common.activation', display: { kind: 'boolean' } },
        // Never on the hand-written page, but they are the page's money fields and the
        // currency kind renders them exactly the way the rest of the app reads money.
        // Hidden by default — surfacing salaries uninvited would change the page's layout —
        // one toggle away in the column manager.
        { key: 'salary', headerKey: 'common.salary', display: { kind: 'currency' }, defaultHidden: true },
        { key: 'extraBonus', headerKey: 'common.extraBonus', display: { kind: 'currency' }, defaultHidden: true },
      ],
      form: GuardedEmployeeForm,
      emptyState: <EmptyState />,
      toPayload: (values: any, params) => ({
        ...shapeEmployeePayload(values),
        salary: Number(values.salary),
        extraBonus: Number(values.extraBonus),
        restaurantId: params.restaurantId,
      }),
      useTitleVars: () => ({ restaurantName: currentRestaurant?.name }),
    }),
    extraActions: [{ labelKey: 'actions.invite', onClick: (row: EmployeeRow) => setInvitingRow(row) }],
  };

  // Keyed by row so opening a different employee remounts the dialog: its email/role/error
  // state then seeds from that row at mount instead of being re-seeded by an effect.
  const inviteModal = (
    <EmployeeInviteModal
      key={invitingRow?.id ?? 'none'}
      row={invitingRow}
      scope={{ orgSlug, restaurantId }}
      onClose={() => setInvitingRow(null)}
    />
  );

  // Sibling of CrudPage, not nested inside its edit modal: the edit modal closes as soon as
  // `submit(values)` resolves (CrudPage's own handleSubmit updates then calls closeForm()),
  // so a revoke-failure notice rendered inside it would close along with it and never be
  // seen. Rendering here — alongside CrudPage in AllEmployees.tsx — keeps it visible
  // regardless of the edit modal's open state, same reasoning as `inviteModal` above.
  const deactivateModal = (
    <Modal
      open={!!pendingDeactivate}
      size="sm"
      modalHeading={t('employee.deactivate.title')}
      primaryButtonText={t('employee.deactivate.confirm')}
      secondaryButtonText={t('actions.cancel')}
      onRequestClose={() => setPendingDeactivate(null)}
      onSecondarySubmit={() => setPendingDeactivate(null)}
      onRequestSubmit={confirmDeactivate}
    >
      <Checkbox
        id="employee-deactivate-revoke"
        labelText={t('employee.deactivate.revoke')}
        checked={revokeAccess}
        onChange={(_event, { checked }) => setRevokeAccess(checked)}
      />
    </Modal>
  );

  // The Account column deliberately keeps reading "active" while any of these are up: a label
  // that says "deactivated" when portal access is still live is exactly the defect spec risk 4
  // exists to remove, so the discrepancy has to stay visible until retried. One notice per
  // failed row (keyed by employee id, see RevokeFailures) — deactivating a second employee
  // while the first revoke is still unresolved must not lose the first notice.
  const revokeFailureNotices = Object.values(revokeFailures).map((failure) => (
    <ActionableNotification
      key={failure.row.id}
      kind="error"
      lowContrast
      title=""
      subtitle={t('employee.deactivate.revokeFailed', { error: failure.error })}
      actionButtonLabel={t('actions.retry')}
      onActionButtonClick={() => revoke(failure.row)}
      onClose={() => {
        setRevokeFailures((prev) => {
          if (!(failure.row.id in prev)) {
            return prev;
          }
          const next = { ...prev };
          delete next[failure.row.id];
          return next;
        });
        return false;
      }}
    />
  ));

  return { page, inviteModal, deactivateModal, revokeFailureNotices };
};
